export type ReportingEventId = string;
export type ReferenceDocumentId = string;
export type TerminologyExtensionId = string;
export type SponsorTermId = string;
export type AnalysisOutputCategorizationId = string;
export type AnalysisOutputCategoryId = string;
export type AnalysisSetId = string;
export type DataSubsetId = string;
export type GroupingFactorId = string;
export type GroupId = string;
export type AnalysisMethodId = string;
export type OperationId = string;
export type ReferencedOperationRelationshipId = string;
export type AnalysisId = string;
export type DisplaySubSectionId = string;
export type OutputId = string;
export type OutputDisplayId = string;
/**
 * The file format of the file containing output from analyses.
 */
export enum OutputFileTypeEnum {
  /** Portable Document Format (PDF) */
  pdf = "pdf",
  /** Rich Text Format (RTF) */
  rtf = "rtf",
  /** Plain text */
  txt = "txt",
}
/**
 * Boolean operators denoting a logical operation (e.g., and, or, not).
 */
export enum ExpressionLogicalOperatorEnum {
  AND = "AND",
  OR = "OR",
  NOT = "NOT",
}
/**
 * Comparison operators indicating how the value of a variable is compared to a (list of) prespecified value(s).
 */
export enum ConditionComparatorEnum {
  /** Is equal to */
  EQ = "EQ",
  /** Is not equal to */
  NE = "NE",
  /** Is greater than */
  GT = "GT",
  /** Is greater than or equal to */
  GE = "GE",
  /** Is less than */
  LT = "LT",
  /** Is less than or equal to */
  LE = "LE",
  /** Is in */
  IN = "IN",
  /** Is not in */
  NOTIN = "NOTIN",
}
/**
 * Types of display section that contain one or more pieces of informational text.
 */
export enum DisplaySectionTypeEnum {
  /** Lines of text appearing above the title and containing additional generic information such as study identifier or page numbering. */
  Header = "Header",
  /** The title is a caption of a table beginning with "Table" followed by sequenced numbers or letters and separated by full stops. The title includes additional lines describing the content. */
  Title = "Title",
  /** Optional text placed in the column header for a column containing row/stub labels. */
  Rowlabel_Header = "Rowlabel Header",
  /** Lines of text placed immediately after the body of the table containing statements explaining the use of terms or values. May be prefaced with a word such as 'Note:'. */
  Legend = "Legend",
  /** Lines of text defining abbreviations, acronyms, and terms used in the body of the display. */
  Abbreviation = "Abbreviation",
  /** Placed after the abbreviations and definitions, if any. Footnotes lead with symbols (asterisk, dagger, double dagger, or section symbol) or superscripted letters (a, b, c) or numbers (1, 2, 3) which are in the table behind units of measure or words. Footnotes add information regarding the associated values where they occur in the body. */
  Footnote = "Footnote",
  /** Lines of text appearing below any footnote and containing additional information such as traceability of the source program or page numbering. */
  Footer = "Footer",
  /** ADDED BY AZ */
  /** Page by */
  PageBy = "PageBy",
  /** Columns in the table */
  Columns = "Columns",
  /** Rows in the table */
  Rows = "Rows",
}

/**
 * The role that the referenced operation's result plays in the calculation of the result of this operation.
 */
export enum OperationRoleEnum {
  /** The dividend of a fraction. */
  NUMERATOR = "NUMERATOR",
  /** The divisor of a fraction. */
  DENOMINATOR = "DENOMINATOR",
}
/**
 * The rationale for performing this analysis. It indicates when the analysis was planned.
 */
export enum AnalysisReasonEnum {
  /** The analysis is specified in a protocol. */
  SPECIFIED_IN_PROTOCOL = "SPECIFIED IN PROTOCOL",
  /** The analysis is specified in a statistical analysis plan. */
  SPECIFIED_IN_SAP = "SPECIFIED IN SAP",
  /** The analysis was triggered by findings in the data. */
  DATA_DRIVEN = "DATA DRIVEN",
  /** The analysis has been requested by a regulatory agency. */
  REQUESTED_BY_REGULATORY_AGENCY = "REQUESTED BY REGULATORY AGENCY",
}
/**
 * The purpose of the analysis within the body of evidence (e.g., section in the clinical study report).
 */
export enum AnalysisPurposeEnum {
  /** The outcome measure(s) of greatest importance specified in the protocol, usually the one(s) used in the power calculation, to evaluate the primary endpoint(s) associated with the primary study objective(s). (After Clinicaltrials.gov) */
  PRIMARY_OUTCOME_MEASURE = "PRIMARY OUTCOME MEASURE",
  /** The outcome measure(s) that is part of a pre-specified analysis plan used to evaluate the secondary endpoint(s) associated with secondary study objective(s) and/or used to evaluate any measure(s) ancillary to the primary or secondary endpoint(s). (After Clinicaltrials.gov). */
  SECONDARY_OUTCOME_MEASURE = "SECONDARY OUTCOME MEASURE",
  /** The outcome measure(s) that is part of a pre-specified analysis plan used to evaluate the exploratory endpoint(s) associated with exploratory study objective(s) and/or any other measures, excluding post-hoc measures, that are a focus of the study. (After clinicaltrials.gov) */
  EXPLORATORY_OUTCOME_MEASURE = "EXPLORATORY OUTCOME MEASURE",
}
/**
 * Extensible ARS enumerations.
 */
export enum ExtensibleTerminologyEnum {
  /** The rationale for performing this analysis. It indicates when the analysis was planned. */
  AnalysisReasonEnum = "AnalysisReasonEnum",
  /** The purpose of the analysis within the body of evidence (e.g., section in the clinical study report). */
  AnalysisPurposeEnum = "AnalysisPurposeEnum",
  /** The role that the referenced operation's result plays in the calculation of the result of this operation. */
  OperationRoleEnum = "OperationRoleEnum",
  /** The file format of the file containing output from analyses. */
  OutputFileTypeEnum = "OutputFileTypeEnum",
}
/**
 * Type of reference for page references.
 */
export enum PageRefTypeEnum {
  /** References are to page numbers. */
  PhysicalRef = "PhysicalRef",
  /** References are to named destinations in the referenced document. */
  NamedDestination = "NamedDestination",
}

/**
 * An object with a name.
 */
export interface NamedObject {
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * A set of analyses and outputs created to meet a specific reporting requirement, such as a CSR or interim analysis.
 */
export interface ReportingEvent extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** An ordinal indicating the version of the identified instance of the class. */
  version?: number;
  /** The main list of the analyses and outputs defined for the reporting event. */
  mainListOfContents: ListOfContents;
  /** Other lists of the analyses and outputs defined for the reporting event. */
  otherListsOfContents?: ListOfContents[];
  /** External documents containing information referenced for the reporting event. */
  referenceDocuments?: ReferenceDocument[];
  /** Any sponsor-defined extensions to extensible terminology. */
  terminologyExtensions?: TerminologyExtension[];
  /** Sets of related implementer-defined categories that can be used to categorize analyses or outputs. */
  analysisOutputCategorizations?: AnalysisOutputCategorization[];
  /** The analysis sets (subject populations) defined for the reporting event. */
  analysisSets?: AnalysisSet[];
  /** Subsets of data identified by selection criteria for inclusion in analysis definitions. */
  dataSubsets?: DataSubset[];
  /** Characteristics used to subdivide the subject population (e.g., treatment, sex, age group) or data records in analysis datasets (e.g., visit, system organ class). */
  analysisGroupings?: GroupingFactor[];
  /** The defined methods used to analyze any analysis variable. */
  methods?: AnalysisMethod[];
  /** The analyses defined for the reporting event. */
  analyses?: Analysis[];
  /** Display section specifications that may be applied to any display. */
  globalDisplaySections?: GlobalDisplaySection[];
  /** The outputs defined for the reporting event. */
  outputs?: Output[];
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** ADDED BY AZ - Matches ExportReportingEvent.Generator */
  generator: Generator;
  /** ADDED BY AZ - Matches ExportReportingEvent.Created */
  created?: string;
  /** ADDED BY AZ - Matches ExportReportingEvent.Hash */
  hash?: string;
  /** ADDED BY AZ - Matches ExportReportingEvent.DataTypeMapping */
  dataTypeMapping: Record<string, string>;
  /** ADDED BY AZ - Legacy annotation variables from legacy templates */
  legacyAnnotationVariables?: DisplayAnnotationList[];
}

/**
 * ADDED BY AZ - Matches ExportGenerator
 */
export interface Generator {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** The url of the tool used to generate the reporting event. */
  url: string;
  /** The name of the tool used to generate the reporting event. */
  name: string;
  /** The version of the tool used to generate the reporting event. */
  version: string;
}

/**
 * ADDED BY AZ - Matches ExportVariable
 */
export interface Variable {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** The domain of the variable (e.g. ADaM). */
  domain: string;
  /** The dataset the variable belongs to. */
  dataset: string;
  /** The name of the variable. */
  name: string;
  /** The type of the variable. */
  type: string;
}

/**
 * ADDED BY AZ
 */
/**
 * List of annotation variables used  
 */
export interface DisplayAnnotationList {
  /** ID for the display this information relates to */
  displayId: string;
  /** UPDATED BY AZ - Matches ExportDisplayAnnotationList.AssociatedVariables (ExportVariable[]) */
  associatedVariables: Variable[];
}

/**
 * A structured list of analyses and outputs included in the reporting event.
 */
export interface ListOfContents extends NamedObject {
  /** A structured list of the analyses and outputs defined for the reporting event. */
  contentsList: NestedList;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * A list of items (analyses or outputs) that may be organized within sub-lists.
 */
export interface NestedList {
  /** Items in the list. Each item may include a reference to an analysis, a reference to an output, or a sub-list. */
  listItems?: OrderedListItem[];
}

/**
 * An abstract class containing attributes used to position class instances within a hierarchical tree.
 */
export interface LevelOrder {
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
}

/**
 * An item (analysis, output or sub-list) ordered relative to other items within a list or sub-list.
 */
export interface OrderedListItem extends NamedObject, LevelOrder {
  /** The identifier of the referenced analysis. */
  analysisId?: AnalysisId;
  /** The identifier of the referenced output. */
  outputId?: OutputId;
  /** A sub-list of items (analyses or outputs) that may be further organized within sub-lists. */
  sublist?: NestedList;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * An external document containing supporting documentation or programming code.
 */
export interface ReferenceDocument extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A path (relative or absolute) indicating the location of the file. */
  location?: string;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * An extensible set of controlled terminology that has been extended with at least one sponsor-defined term.
 */
export interface TerminologyExtension {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** The name of the extensible enumeration. */
  enumeration?: string;
  /** The sponsor-defined terms added to the extensible terminology. */
  sponsorTerms: SponsorTerm[];
}

/**
 * A sponsor-defined term that is included in an extensible set of controlled terminology.
 */
export interface SponsorTerm {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** The specific value expected for submissions. */
  submissionValue: string;
  /** A textual description of the instance of the class. */
  description?: string;
}

/**
 * The term used for an attribute whose terminology is extensible.
 */
export interface ExtensibleTerminologyTerm {
  /** One of the permissible values from the referenced enumeration. */
  controlledTerm?: string;
  /** The identifier of the referenced sponsor term. */
  sponsorTermId?: SponsorTermId;
}

/**
 * A set of related implementer-defined categories that can be used to categorize analyses or outputs.
 */
export interface AnalysisOutputCategorization {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** Implementer-defined categories of analyses/outputs, each of which may include one or more sub-categorization. */
  categories: AnalysisOutputCategory[];
}

/**
 * An implementer-defined category of analyses/outputs, which may include one or more sub-categorization.
 */
export interface AnalysisOutputCategory {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** Sets of related implementer-defined sub-categories that can be used to categorize analyses or outputs. */
  subCategorizations?: AnalysisOutputCategorization[];
}

/**
 * Selection criteria defined as either a simple condition ([variable] [comparator] [value(s)]) or a compound expression that may combine simple conditions or compound expressions.
 * From the original ARS definition
 *       export interface WhereClause extends LevelOrder {
 *         condition?: WhereClauseCondition;
 *         compoundExpression?: WhereClauseCompoundExpression;
 *         level: number;
 *         order: number;
 *       }
 */
export interface WhereClauseWithCondition extends LevelOrder {
  /** A simple selection criterion exressed as [dataset].[variable] [comparator] [value(s)] */
  condition: WhereClauseCondition;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
}
export interface WhereClauseWithCompoundExpression extends LevelOrder {
  /** A compound expression that combines or negates where clauses. */
  compoundExpression: WhereClauseCompoundExpression;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
}
export type WhereClause = WhereClauseWithCondition | WhereClauseWithCompoundExpression;

/**
 * A simple selection criterion exressed as [dataset].[variable] [comparator] [value(s)]
 */
export interface WhereClauseCondition {
  /** The name of the analysis dataset. */
  dataset?: string;
  /** The name of the variable. */
  variable?: string;
  /** Comparison operator indicating how the variable is compared to the value(s). */
  comparator?: ConditionComparatorEnum;
  /** The value(s) for comparison with the variable. */
  value?: string[];
}

/**
 * An abstract class indicating an identified where clause (i.e., `AnalysisSet`, `DataSubset` or `Group`) referenced by identifier (`subClauseId`) as the sub-clause in a compound expression.
 */
export interface ReferencedWhereClause extends LevelOrder {
  /** The identifier of the analysis set, data subset or group referenced in the compound expression. */
  subClauseId: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
}

/**
 * ADDED BY AZ - An abstract representation of a CASE WHEN item and condition.
 */
export interface CaseWhenItem<T> {
  /** The unique identifier for the CASE WHEN item. */
  id: string;
  /** The display pattern emitted for the CASE WHEN item. */
  displayPattern: T;
  /** The result pattern emitted for the CASE WHEN item. */
  resultPattern: string;
  /** The optional condition under which the CASE WHEN item is applied. */
  when?: WhereClause | null;
}

/**
 * ADDED BY AZ - An abstract representation of a CASE WHEN condition containing multiple CASE WHEN items.
 */
export interface CaseWhenCondition<T> {
  /** The unique identifier for the CASE WHEN condition. */
  id: string;
  /** The list of CASE WHEN items contained within this condition. */
  caseWhenItems: CaseWhenItem<T>[];
}

/**
 * An abstract class containing all attributes that may be specified for a sub-clause of a compound expression. In practice, each sub-clause will be either a fully-defined `WhereClause` (containing either a `condition` or a `compoundExpression`) or a `ReferencedWhereClause` (containing a `subClauseId`).
 * From the original ARS definition
 *     export interface SubClause extends LevelOrder, WhereClause, ReferencedWhereClause {
 *       level: number;
 *       order: number;
 *       condition?: WhereClauseCondition;
 *       compoundExpression?: WhereClauseCompoundExpression;
 *       subClauseId: string;
 *     }
 */

export interface SubClauseWithCondition
  extends LevelOrder, WhereClauseWithCondition, ReferencedWhereClause {
  /** Surrogate key for this where clause */
  id: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** A simple selection criterion exressed as [dataset].[variable] [comparator] [value(s)] */
  condition: WhereClauseCondition;
  /** Blank subClauseId */
  subClauseId: "";
}
export interface SubClauseWithCompoundExpression
  extends LevelOrder, WhereClauseWithCompoundExpression, ReferencedWhereClause {
  /** Surrogate key for this where clause */
  id: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** A compound expression that combines or negates where clauses. */
  compoundExpression: WhereClauseCompoundExpression;
  /** Blank subClauseId */
  subClauseId: "";
}
export interface SubClauseWithReference extends LevelOrder, ReferencedWhereClause {
  /** Surrogate key for this where clause */
  id: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** The identifier of the analysis set, data subset or group referenced in the compound expression. */
  subClauseId: string;
}
export type SubClause =
  SubClauseWithCondition | SubClauseWithCompoundExpression | SubClauseWithReference;

/**
 * An abstract class representing a compound expression consisting of either two or more sub-clauses combined with the `AND` or `OR` logical operator, or a single sub-clause negated with the `NOT` logical operator. Each sub-clause is either a fully-defined `WhereClause` (containing either a `condition` or a `compoundExpression`) or a `ReferencedWhereClause` (containing a `subClauseId`).
 */
export interface WhereClauseCompoundExpression {
  /** The boolean operator that is used to combine (AND, OR) or negate (NOT) the where claus(s) in the compound expression. */
  logicalOperator: ExpressionLogicalOperatorEnum;
  /** A list of one or more where clauses (selection criteria) to be combined or negated. */
  whereClauses?: SubClause[];
}

/**
 * A set of subjects whose data are to be included in the main analyses. This should be defined in the statistical section of the protocol. NOTE: There are a number of potential analysis sets, including, for example, the set based upon the intent-to-treat principle. [ICH E9]
 * Extended from the original ARS definition
 *       export interface AnalysisSet extends NamedObject, WhereClause {
 *         id: string;
 *         condition?: WhereClauseCondition;
 *         compoundExpression?: CompoundSetExpression;
 *         name: string;
 *         description?: string;
 *         label?: string;
 *         level: number;
 *         order: number;
 *       }
 */
export interface AnalysisSetWithCondition extends NamedObject, WhereClauseWithCondition {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A simple selection criterion exressed as [dataset].[variable] [comparator] [value(s)] */
  condition: WhereClauseCondition;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}
export interface AnalysisSetWithCompoundExpression
  extends NamedObject, WhereClauseWithCompoundExpression {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A simple selection criterion exressed as [dataset].[variable] [comparator] [value(s)] */
  compoundExpression: CompoundSetExpression;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}
export type AnalysisSet = AnalysisSetWithCondition | AnalysisSetWithCompoundExpression;

/**
 * An `AnalysisSet` referenced by identifier (`subClauseId`) as the sub-clause of a compound expression.
 */
export interface ReferencedAnalysisSet extends ReferencedWhereClause {
  /** The identifier of the analysis set, data subset or group referenced in the compound expression. */
  subClauseId: AnalysisSetId;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
}

/**
 * A compound expression consisting of either two or more sub-clauses combined with the `AND` or `OR` logical operator, or a single sub-clause negated with the `NOT` logical operator. Each sub-clause is either a fully-defined `WhereClause` (containing either a `condition` or a `compoundExpression`) or a `ReferencedAnalysisSet` (containing a `subClauseId` indicating the `id` of the referenced `AnalysisSet`).
 */
export interface CompoundSetExpression extends WhereClauseCompoundExpression {
  /** The boolean operator that is used to combine (AND, OR) or negate (NOT) the where claus(s) in the compound expression. */
  logicalOperator: ExpressionLogicalOperatorEnum;
  /** A list of one or more where clauses (selection criteria) to be combined or negated. */
  whereClauses?: SubClause[];
}

/**
 * A subset of data identified by selection criteria for inclusion in the analysis.
 * Extended from the original ARS definition
 *     export interface DataSubset extends NamedObject, WhereClause {
 *       id: string;
 *       condition?: WhereClauseCondition;
 *       compoundExpression?: CompoundSubsetExpression;
 *       name: string;
 *       description?: string;
 *       label?: string;
 *       level: number;
 *       order: number;
 *     }
 */
export interface DataSubsetWithCondition extends NamedObject, WhereClauseWithCondition {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A simple selection criterion exressed as [dataset].[variable] [comparator] [value(s)] */
  condition: WhereClauseCondition;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** Unique subjects which meet the selection criteria. */
  uniqueSubjects?: boolean;
  /** Unique picker variables */
  uniqueVariables?: string[];
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}
export interface DataSubsetWithCompoundExpression
  extends NamedObject, WhereClauseWithCompoundExpression {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A compound expression that combines or negates where clauses. */
  compoundExpression: CompoundSubsetExpression;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** Unique subjects which meet the selection criteria. */
  uniqueSubjects?: boolean;
  /** Unique picker variables */
  uniqueVariables?: string[];
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}
export type DataSubset = DataSubsetWithCondition | DataSubsetWithCompoundExpression;

/**
 * A `DataSubset` referenced by identifier (`subClauseId`) as the sub-clause of a compound expression.
 */
export interface ReferencedDataSubset extends ReferencedWhereClause {
  /** The identifier of the analysis set, data subset or group referenced in the compound expression. */
  subClauseId: DataSubsetId;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
}

/**
 * A compound expression consisting of either two or more sub-clauses combined with the `AND` or `OR` logical operator, or a single sub-clause negated with the `NOT` logical operator. Each sub-clause is either a fully-defined `WhereClause` (containing either a `condition` or a `compoundExpression`) or a `ReferencedDataSubset` (containing a `subClauseId` indicating the `id` of the referenced `DataSubset`).
 */
export interface CompoundSubsetExpression extends WhereClauseCompoundExpression {
  /** The boolean operator that is used to combine (AND, OR) or negate (NOT) the where claus(s) in the compound expression. */
  logicalOperator: ExpressionLogicalOperatorEnum;
  /** A list of one or more where clauses (selection criteria) to be combined or negated. */
  whereClauses?: SubClause[];
}

/**
 * A factor used to subdivide either the subject population or data records in an analysis dataset for analysis.
 * Extended from the original ARS definition
 *       export interface GroupingFactor extends NamedObject {
 *         id: string;
 *         groupingDataset?: string;
 *         groupingVariable?: string;
 *         dataDriven: boolean;
 *         groups?: Group[];
 *         name: string;
 *         description?: string;
 *         label?: string;
 *       }
 */
export interface GroupingFactorDataDriven extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** For groupings based on a single variable, a reference to the dataset containing the variable upon which grouping is based. */
  groupingDataset: string;
  /** For groupings based on a single variable, a reference to the dataset variable upon which grouping is based. */
  groupingVariable: string;
  /** Optional order variable */
  orderVariable?: string;
  /** Optional unit variable */
  unitVariable?: string;
  /** Optional other variable */
  otherVariable?: string;
  /** Indicates whether the groups defined by the grouping are prespecified (false) or obtained from distinct data values of the groupingVariable (true). */
  dataDriven: true;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** ADDED BY AZ - Values derived from distinct values of the grouping variable. Matches ExportGroupingFactor.DataValues. */
  dataValues: string[];
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}
export interface GroupingFactorWithGroups extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** This is not data driven */
  dataDriven: false;
  /** The pre-specified groups within the grouping. */
  groups: Group[];
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}
export type GroupingFactor = GroupingFactorDataDriven | GroupingFactorWithGroups;

/**
 * A subdivision of the subject population or analysis dataset record set based on a defined factor.
 * Extended from the original ARS definition
 *     export interface Group extends NamedObject, WhereClause {
 *       id: string;
 *       condition?: WhereClauseCondition;
 *       compoundExpression?: CompoundGroupExpression;
 *       name: string;
 *       description?: string;
 *       label?: string;
 *       level: number;
 *       order: number;
 *     }
 */
export interface GroupWithCondition extends NamedObject, WhereClauseWithCondition {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A simple selection criterion exressed as [dataset].[variable] [comparator] [value(s)] */
  condition: WhereClauseCondition;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}
export interface GroupWithCompoundExpression
  extends NamedObject, WhereClauseWithCompoundExpression {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** A compound expression that combines or negates where clauses. */
  compoundExpression: CompoundGroupExpression;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}
export type Group = GroupWithCondition | GroupWithCompoundExpression;

/**
 * A `Group` referenced by identifier (`subClauseId`) as the sub-clause of a compound expression.
 */
export interface ReferencedGroup extends ReferencedWhereClause {
  /** The identifier of the analysis set, data subset or group referenced in the compound expression. */
  subClauseId: GroupId;
  /** The level of the entry within a hierarchical structure. */
  level: number;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
}

/**
 * A compound expression consisting of either two or more sub-clauses combined with the `AND` or `OR` logical operator, or a single sub-clause negated with the `NOT` logical operator. Each sub-clause is either a fully-defined `WhereClause` (containing either a `condition` or a `compoundExpression`) or a `ReferencedGroup` (containing a `subClauseId` indicating the `id` of the referenced `Group`).
 */
export interface CompoundGroupExpression extends WhereClauseCompoundExpression {
  /** The boolean operator that is used to combine (AND, OR) or negate (NOT) the where claus(s) in the compound expression. */
  logicalOperator: ExpressionLogicalOperatorEnum;
  /** A list of one or more where clauses (selection criteria) to be combined or negated. */
  whereClauses?: SubClause[];
}

/**
 * A set of one or more statistical operations.
 */
export interface AnalysisMethod extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** References to external documents containing additional information. */
  documentRefs?: DocumentReference[];
  /** The calculations performed for the method. Each operation generates a statistical result. */
  operations: Operation[];
  /** Template programming statements used to perform the statistical operations for any analysis that uses this method. */
  codeTemplate?: AnalysisProgrammingCodeTemplate;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** ADDED FOR AZ - Display only - Individually disable editing */
  disableEditing?: boolean;
  /** ADDED FOR AZ - Display only - Source hierarchy id */
  fromHierarchyId?: string;
}

/**
 * A reference to an external document.
 */
export interface DocumentReference {
  /** The identifier of the referenced document. */
  referenceDocumentId: ReferenceDocumentId;
  /** A list of references to specific parts of a document, which may be referenced as a list of one or more page numbers, a range of page numbers, or a list of named destinations in the document (e.g. bookmarks). */
  pageRefs?: PageRef[];
}

/**
 * A reference to a specific part of a document as indicated by a list of one or more page numbers, a range of page numbers, or a list of named destinations in the document (e.g. bookmarks).
 */
export interface PageRef {
  /** The type of reference for page references. */
  refType: PageRefTypeEnum;
  /** Alternative label to provide a more specific and description to a page link. */
  label?: string;
  /** One or more named document references which each correspond with a page. */
  pageNames?: string[];
  /** One or more page numbers. */
  pageNumbers?: number[];
  /** The page number of the first page in a range of pages. */
  firstPage?: number;
  /** The page number of the last page in a range of pages. */
  lastPage?: number;
}

/**
 * One or more individual pages in the reference document, referenced by page number.
 */
export interface PageNumberListRef extends PageRef {
  /** The type of reference for page references. */
  refType: PageRefTypeEnum;
  /** Alternative label to provide a more specific and description to a page link. */
  label?: string;
  /** NOT USED */
  pageNames?: string[];
  /** One or more page numbers. */
  pageNumbers: number[];
  /** NOT USED */
  firstPage?: number;
  /** NOT USED */
  lastPage?: number;
}

/**
 * A range of pages in the reference document, indicated by the first and last page number in the range.
 */
export interface PageNumberRangeRef extends PageRef {
  /** The type of reference for page references. */
  refType: PageRefTypeEnum;
  /** Alternative label to provide a more specific and description to a page link. */
  label?: string;
  /** NOT USED */
  pageNames?: string[];
  /** NOT USED */
  pageNumbers?: number[];
  /** The page number of the first page in a range of pages. */
  firstPage: number;
  /** The page number of the last page in a range of pages. */
  lastPage: number;
}

/**
 * One or more pages in the reference document, referenced by named destination.
 */
export interface PageNameRef extends PageRef {
  /** The type of reference for page references. */
  refType: PageRefTypeEnum;
  /** Alternative label to provide a more specific and description to a page link. */
  label?: string;
  /** One or more named document references which each correspond with a page. */
  pageNames: string[];
  /** NOT USED */
  pageNumbers?: number[];
  /** NOT USED */
  firstPage?: number;
  /** NOT USED */
  lastPage?: number;
}

/**
 * A statistical operation that produces a single analysis result value as part of an analysis method.
 */
export interface Operation extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** Relationships to other operations indicating how the result of the referenced operation are used in the calculation of the result for this operation. */
  referencedOperationRelationships?: ReferencedOperationRelationship[];
  /** The default pattern or format to apply to the result for display. */
  resultPattern?: string;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** ADDED BY AZ - display property only, not part of ExportOperation */
  denominatorOnly?: boolean;
  /** ADDED BY AZ - Persisted on AnalysisMethodOperation, not ExportOperation. Initial DATASET.VARIABLE for new table operations; an empty string disables the default. Saved table targets are never replaced. */
  defaultTargetVariable?: string;
}

/**
 * A reference to a statistical operation whose results are used in the calculation of the result for this operation.
 */
export interface ReferencedOperationRelationship {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** The role that the referenced operation's result plays in the calculation of the result of the parent operation. */
  referencedOperationRole: ExtensibleTerminologyTerm;
  /** The identifier of the referenced operation. */
  operationId: OperationId;
  /** The identifier of the referenced analysis. */
  analysisId?: AnalysisId;
  /** A textual description of the instance of the class. */
  description?: string;
}

/**
 * The role that the referenced operation's result plays in the calculation of the result of this operation.
 */
export interface OperationRole extends ExtensibleTerminologyTerm {
  /** One of the permissible values from the referenced enumeration. */
  controlledTerm: string;
  /** NOT USED */
  sponsorTermId?: SponsorTermId;
}

/**
 * The sponsor-defined role that the referenced operation's result plays in the calculation of the result of this operation.
 */
export interface SponsorOperationRole extends ExtensibleTerminologyTerm {
  /** NOT USED */
  controlledTerm?: string;
  /** A reference to a sponsor term in the TerminologyExtension with enumeration=OperationRoleEnum */
  sponsorTermId: SponsorTermId;
}

/**
 * Programming statements and/or a reference to a used as a template for creation of a program to perform method operations for a specific analysis.
 */
export interface AnalysisProgrammingCodeTemplate {
  /** The name and version of the computer language used for the actual programming statements provided. */
  context: string;
  /** Programming statements used to perform the specific analysis. */
  code?: string;
  /** A reference to the document containing programming code. */
  documentRef?: DocumentReference;
  /** Parameters whose values will be used to generate or execute the programming code for a specific analysis. */
  parameters?: TemplateCodeParameter[];
}

/**
 * A replacement parameter whose value is substituted in template programming code to create statements required for a specific analysis.
 */
export interface CodeParameter extends NamedObject {
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * A replacement parameter whose value is substituted in template programming code to create statements required for a specific analysis.
 * Originally defined as:
 * export interface TemplateCodeParameter extends CodeParameter {
 *   valueSource?: string;
 *   value?: string[];
 *   name: string;
 *   description?: string;
 *   label?: string;
 * }
 *
 * UPDATED BY AZ
 */
export interface TemplateCodeParameter extends CodeParameter {
  /** UPDATED BY AZ */
  /** The source of the parameter value. */
  valueSource: "groupingFactor" | "operation";
  /** Default value */
  value?: string[];
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** ADDED BY AZ */
  /** Type of parameter for FE rendering */
  parameterType:
    | "string"
    | "number"
    | "boolean"
    | "stringList"
    | "numberList"
    | "select"
    | "multiSelect"
    | "dataset.variable";
  /** Available values for the parameter. */
  availableValues?: string[];
}

/**
 * An analysis that is designed to meet a requirement of the reporting event. Each analysis is defined as a set of specifications, including:
 * The analysis variable that is the subject of the analysis.
 * The analysis method (set of statistical operations) that is performed for the analysis variable.
 * The analysis set (subject population) for which the analysis is performed.
 * The subset of data records on which the analysis is performed (optional).
 * One or more factors by which either subjects or data records are grouped for analysis (optional).
 */
export interface Analysis extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** An ordinal indicating the version of the identified instance of the class. */
  version?: number;
  /** The rationale for performing this analysis. It indicates when the analysis was planned. */
  reason: ExtensibleTerminologyTerm;
  /** The purpose of the analysis within the body of evidence (e.g., section in the clinical study report). */
  purpose: ExtensibleTerminologyTerm;
  /** References to external documents containing additional information. */
  documentRefs?: DocumentReference[];
  /** References to any implementer-defined categories that apply to the analysis. */
  categoryIds?: AnalysisOutputCategoryId[];
  /** The name of the analysis dataset. */
  dataset?: string;
  /** The name of the variable. */
  variable?: string;
  /** The identifier of the referenced analysis set. */
  analysisSetId?: AnalysisSetId;
  /** The identifier of the referenced data subset. */
  dataSubsetId?: DataSubsetId;
  /** An ordered list of grouping factors used in the analysis. */
  orderedGroupings?: OrderedGroupingFactor[];
  /** A reference to the set of one or more statistical operations performed for the analysis. */
  methodId: AnalysisMethodId;
  /** Indications of which analysis contains the results for each referenced operation. */
  referencedAnalysisOperations?: ReferencedAnalysisOperation[];
  /** Programming statements and/or a reference to the program used to perform the specific analysis. */
  programmingCode?: AnalysisOutputProgrammingCode;
  /** The results of the analysis. */
  results?: OperationResult[];
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * The rationale for performing this analysis.
 */
export interface AnalysisReason extends ExtensibleTerminologyTerm {
  /** One of the permissible values from the referenced enumeration. */
  controlledTerm: string;
  /** NOT USED */
  sponsorTermId?: SponsorTermId;
}

/**
 * The sponsor-defined rationale for performing this analysis.
 */
export interface SponsorAnalysisReason extends ExtensibleTerminologyTerm {
  /** NOT USED */
  controlledTerm?: string;
  /** A reference to a sponsor term in the TerminologyExtension with enumeration=AnalysisReasonEnum */
  sponsorTermId: SponsorTermId;
}

/**
 * The purpose of the analysis within the body of evidence (e.g., section in the clinical study report).
 */
export interface AnalysisPurpose extends ExtensibleTerminologyTerm {
  /** One of the permissible values from the referenced enumeration. */
  controlledTerm: string;
  /** NOT USED */
  sponsorTermId?: SponsorTermId;
}

/**
 * The sponsor-defined purpose of the analysis within the body of evidence (e.g., section in the clinical study report).
 */
export interface SponsorAnalysisPurpose extends ExtensibleTerminologyTerm {
  /** NOT USED */
  controlledTerm?: string;
  /** A reference to a sponsor term in the TerminologyExtension with enumeration=AnalysisPurposeEnum */
  sponsorTermId: SponsorTermId;
}

/**
 * A reference to a defined factor by which subjects or data records are grouped for the analysis, ordered with respect to other grouping factors.
 */
export interface OrderedGroupingFactor {
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** The identifier of the referenced subject or data grouping factor. */
  groupingId: GroupingFactorId;
  /** Indicates whether a result is expected for each group in the grouping. */
  resultsByGroup: boolean;
}

/**
 * An indication of the analysis that contains results of a referenced operation.
 */
export interface ReferencedAnalysisOperation {
  /** The identifier of the defined referenced operation relationship. */
  referencedOperationRelationshipId: ReferencedOperationRelationshipId;
  /** The identifier of the referenced analysis. */
  analysisId: AnalysisId;
}

/**
 * Programming statements and/or a reference to the program used to perform a specific analysis or create a specific output.
 */
export interface AnalysisOutputProgrammingCode {
  /** The name and version of the computer language used for the actual programming statements provided. */
  context: string;
  /** Programming statements used to perform the specific analysis. */
  code?: string;
  /** A reference to the document containing programming code. */
  documentRef?: DocumentReference;
  /** Parameter values used to generate or execute the programming code. */
  parameters?: AnalysisOutputCodeParameter[];
}

/**
 * A parameter whose value is used in programming code for a specific analysis or output.
 */
export interface AnalysisOutputCodeParameter extends CodeParameter {
  /** The value of the parameter. */
  value: string[];
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * The result of an analysis method operation performed on a subdivision of subjects or data records.
 */
export interface OperationResult {
  /** The identifier of the referenced operation. */
  operationId: OperationId;
  /** The group values associated with the result. */
  resultGroups?: ResultGroup[];
  /** The raw result value (e.g., with no rounding applied). */
  rawValue?: string;
  /** The result value formatted for display according to the resultPattern. */
  formattedValue?: string;
}

/**
 * For the specified grouping factor, an indication of the specific group of subjects or data records associated with the analysis result.
 */
export interface ResultGroup {
  /** The identifier of the referenced subject or data grouping factor. */
  groupingId: GroupingFactorId;
  /** The identifier of a referenced predefined group within a grouping. */
  groupId?: GroupId;
  /** The data value used as a group within a data-driven grouping. */
  groupValue?: string;
}

/**
 * A global definition for part of a tabular display containing one or more pieces of informational text that may be used in multiple displays.
 */
export interface GlobalDisplaySection {
  /** The type of display section that contains one or more pieces of informational text. */
  sectionType?: DisplaySectionTypeEnum;
  /** A list of defined pieces of information text that may be displayed in display sections of the same type. */
  subSections?: DisplaySubSection[];
}

/**
 * An occurrence of a display section containing text.
 */
export interface DisplaySubSection {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** The text to be displayed in the display section. */
  text: string;
  /** ADDED BY AZ - Ordered list of subsections within the display subsection. */
  orderedSubSections?: OrderedDisplaySubSection[];
  /** ADDED BY AZ - The identifier of the operation associated with the subsection. */
  operationId?: OperationId;
  /** ADDED BY AZ - The identifier of the analysis associated with the subsection. */
  analysisId?: AnalysisId;
  /** ADDED BY AZ - The identifier of the grouping factor associated with the subsection. */
  groupingFactorId?: GroupingFactorId;
  /** ADDED BY AZ - The identifier of the group associated with the subsection. */
  groupId?: GroupId;
  /** ADDED BY AZ - Indicates whether the subsection label is hidden. */
  labelHidden?: boolean;
  /** ADDED BY AZ - Indicates whether the subsection is inlined with the previous level. */
  inlinedWithPreviousLevel?: boolean;
  /** ADDED BY AZ - Indicates whether the grouping label is inlined. */
  inlinedGroupLabel?: boolean;
}

/**
 * A report of results and their evaluation based on planned analyses performed during the course of a trial.
 */
export interface Output extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** An ordinal indicating the version of the identified instance of the class. */
  version?: number;
  /** Specifications of output files. */
  fileSpecifications?: OutputFile[];
  /** An ordered list of the displays included in the output. */
  displays: OrderedDisplay[];
  /** References to any implementer-defined categories that apply to the output. */
  categoryIds?: AnalysisOutputCategoryId[];
  /** References to external documents containing additional information. */
  documentRefs?: DocumentReference[];
  /** Programming statements and/or a reference to the program used to perform the specific output. */
  programmingCode?: AnalysisOutputProgrammingCode;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
  /** ADDED BY AZ - The table of contents number associated with the output. */
  tocNumber?: string;
  /** ADDED BY AZ - The identifier of the TLF item associated with the output. */
  tlfItemId?: string;
  /** ADDED BY AZ - The program name. */
  programName?: string;
  /** ADDED BY AZ - The AZSOL identifier associated with the output. */
  azsolId?: string;
  /** ADDED BY AZ - The output suffix. */
  suffix?: string;
  /** ADDED BY AZ - The output name used by the source TLF item. */
  outputName?: string;
  /** ADDED BY AZ - The output type. */
  type?: string;
  /** ADDED BY AZ - The domain associated with the output. */
  domain?: string;
  /** ADDED BY AZ - Notes about standards for the output. */
  standardsNotes?: string;
  /** ADDED BY AZ - Notes for the programmer responsible for the output. */
  programmerNotes?: string;
  /** ADDED BY AZ - The number of row headers in the output. */
  rowHeaderCount?: number;
  /** ADDED BY AZ - The date and time when the output was created. */
  createdAt?: string;
  /** ADDED BY AZ - The date and time when the output was last updated. */
  lastUpdatedAt?: string;
}

/**
 * A file containing analysis output displays.
 */
export interface OutputFile extends NamedObject {
  /** The format of the output file. */
  fileType?: ExtensibleTerminologyTerm;
  /** A path (relative or absolute) indicating the location of the file. */
  location?: string;
  /** Reference to the specification of the style used for the output. */
  style?: string;
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * The file format of the file containing output from analyses.
 */
export interface OutputFileType extends ExtensibleTerminologyTerm {
  /** One of the permissible values from the referenced enumeration. */
  controlledTerm: string;
  /** NOT USED */
  sponsorTermId?: SponsorTermId;
}

/**
 * The sponsor-defined file format of the file containing output from analyses.
 */
export interface SponsorOutputFileType extends ExtensibleTerminologyTerm {
  /** NOT USED */
  controlledTerm?: string;
  /** A reference to a sponsor term in the TerminologyExtension with enumeration=OutputFileTypeEnum */
  sponsorTermId: SponsorTermId;
}

/**
 * A display ordered with respect to other displays in an analysis output.
 */
export interface OrderedDisplay {
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** A display contained in the output. */
  display: OutputDisplay;
}

/**
 * A tabular representation of the results of one or more analyses.
 */
export interface OutputDisplay extends NamedObject {
  /** The assigned identifying value for the instance of the class. */
  id: string;
  /** An ordinal indicating the version of the identified instance of the class. */
  version?: number;
  /** Display description which uniquely identifies the display in the report. */
  displayTitle?: string;
  /** ADDED BY AZ - Replacement lists applied to groups in the output display. */
  groupReplacementsLists?: ExternalReplacement[];
  /** ADDED BY AZ - Individual text replacements applied to the output display. */
  singleReplacements?: SingleReplacement[];
  /** The parts of the display containing one or more pieces of informational text (e.g. title, footnote). */
  displaySections?: DisplaySection[];
  /** The name for the instance of the class. */
  name: string;
  /** A textual description of the instance of the class. */
  description?: string;
  /** A short informative description that may be used for display. */
  label?: string;
}

/**
 * ADDED BY AZ - A replacement list included in an exported output display.
 */
export interface ExternalReplacement {
  /** ADDED BY AZ - The replacement list identifier. */
  id: string;
  /** ADDED BY AZ - The optional name of the replacement list. */
  givenName?: string;
  /** ADDED BY AZ - The text to replace. */
  oldText: string;
  /** ADDED BY AZ - The replacement text values. */
  replacedTexts: string[];
  /** ADDED BY AZ - Nested replacement lists. */
  subLists?: ExternalReplacement[];
}

/**
 * ADDED BY AZ - A single text replacement included in an exported output display.
 */
export interface SingleReplacement {
  /** ADDED BY AZ - The replacement identifier. */
  id: string;
  /** ADDED BY AZ - The original text. */
  originalText: string;
  /** ADDED BY AZ - The replacement text. */
  replacedText: string;
}

/**
 * A part of a tabular display containing one or more pieces of informational text (e.g., title, footnote).
 * Original non-AZ ARS definition:
 * export interface DisplaySection {
 *   sectionType?: string;
 *   orderedSubSections?: OrderedDisplaySubSection[];
 * }
 */
export interface DisplaySection {
  /** The type of display section that contains one or more pieces of informational text. */
  sectionType: DisplaySectionTypeEnum;
  /** An ordered list of the informational text to display in the display section. */
  orderedSubSections: OrderedDisplaySubSection[];
}

/**
 * A single subsection ordered with respect to other subsections in the same section of a display.
 */
export interface OrderedDisplaySubSection {
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /* UPDATED BY AZ - Either a defined piece of information text to display in a display section, or a reference to a defined piece of information text to display in a display section. Only one of `subSection` or `subSectionId` should be provided. */
  subSection?: DisplaySubSection;
  /** The identifier of the referenced subsection. */
  subSectionId?: DisplaySubSectionId;
  /** ADDED BY AZ - The lowercase cell alignment exported by the backend, defaulting to "default". */
  alignment?: string;
  /** ADDED BY AZ - The indentation level of the subsection, or null when unspecified. */
  indent?: number | null;
  /** ADDED BY AZ - The number of columns spanned by the subsection, or null when unspecified. */
  colSpan?: number | null;
  /** ADDED BY AZ - The number of rows spanned by the subsection, or null when unspecified. */
  rowSpan?: number | null;
  /** Text alignment for the subsection header. */
  headerTextAlignment?: string;
  /** Text alignment for the subsection body. */
  bodyTextAlignment?: string;
  /** Width of the subsection column in pixels. */
  columnWidth?: number;
  /** Percentage used to align decimal values. */
  decimalAlignPercent?: number;
  /** ADDED BY AZ - Result pattern for the ordered subsection. */
  resultPattern?: string;
  /** Variables referenced by the subsection. */
  variables?: Variable[];
  /** Conditional display patterns associated with the subsection. */
  caseWhenCondition?: CaseWhenCondition<string>;
}

/**
 * A subsection ordered with respect to other subsections of the same type.
 */
export interface OrderedSubSection extends OrderedDisplaySubSection {
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** A defined piece of information text to display in a display section. */
  subSection: DisplaySubSection;
  /** NOT USED */
  subSectionId?: DisplaySubSectionId;
  /** ADDED BY AZ - Redeclared inherited fields included in the backend Export model. */
  alignment?: OrderedDisplaySubSection["alignment"];
  indent?: OrderedDisplaySubSection["indent"];
  colSpan?: OrderedDisplaySubSection["colSpan"];
  rowSpan?: OrderedDisplaySubSection["rowSpan"];
  headerTextAlignment?: OrderedDisplaySubSection["headerTextAlignment"];
  bodyTextAlignment?: OrderedDisplaySubSection["bodyTextAlignment"];
  columnWidth?: OrderedDisplaySubSection["columnWidth"];
  decimalAlignPercent?: OrderedDisplaySubSection["decimalAlignPercent"];
  resultPattern?: OrderedDisplaySubSection["resultPattern"];
  variables?: OrderedDisplaySubSection["variables"];
  caseWhenCondition?: OrderedDisplaySubSection["caseWhenCondition"];
  /** ADDED BY AZ - Attached ids from the analyses/analysis groupings */
  referencedAnalysisId?: AnalysisId;
  referencedGroupingFactorId?: GroupingFactorId;
}

/**
 * A reference to a subsection defined either globally or in another display section, ordered with respect to other subsections of the same type.
 */
export interface OrderedSubSectionRef extends OrderedDisplaySubSection {
  /** The ordinal of the instance with respect to other instances. */
  order: number;
  /** NOT USED */
  subSection?: DisplaySubSection;
  /** The identifier of the referenced subsection. */
  subSectionId: DisplaySubSectionId;
  /** ADDED BY AZ - Redeclared inherited fields included in the backend Export model. */
  alignment?: OrderedDisplaySubSection["alignment"];
  indent?: OrderedDisplaySubSection["indent"];
  colSpan?: OrderedDisplaySubSection["colSpan"];
  rowSpan?: OrderedDisplaySubSection["rowSpan"];
  headerTextAlignment?: OrderedDisplaySubSection["headerTextAlignment"];
  bodyTextAlignment?: OrderedDisplaySubSection["bodyTextAlignment"];
  columnWidth?: OrderedDisplaySubSection["columnWidth"];
  decimalAlignPercent?: OrderedDisplaySubSection["decimalAlignPercent"];
  resultPattern?: OrderedDisplaySubSection["resultPattern"];
  variables?: OrderedDisplaySubSection["variables"];
  caseWhenCondition?: OrderedDisplaySubSection["caseWhenCondition"];
  /** ADDED BY AZ - Attached ids from the analyses/analysis groupings */
  referencedAnalysisId?: AnalysisId;
  referencedGroupingFactorId?: GroupingFactorId;
}
